import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { newsApi } from '../services/api';
import { Newspaper, Zap, TrendingUp, Cpu, ExternalLink, Clock,TrendingDown } from 'lucide-react';

const Insights = () => {
    const [newsData, setNewsData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadNews = async () => {
            const data = await newsApi.getLatestAINews();
            setNewsData(data);
            setLoading(false);
        };
        loadNews();
    }, []);

    if (loading) return (
        <div className="h-screen flex items-center justify-center bg-slate-50">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-600"></div>
        </div>
    );

    return (
        <PageLayout>
            <div className="max-w-7xl mx-auto px-6 py-12">

                {/* --- HEADER SECTION --- */}
                <div className="mb-12 border-b border-slate-200 pb-8">
                    <div className="flex items-center gap-2 text-blue-600 font-bold text-xs uppercase tracking-[0.2em] mb-3">
                        <TrendingUp size={16} /> Global AI Intelligence
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
                        AI News & <span className="text-blue-600">Market Insights</span>
                    </h1>
                    <div className="flex items-center gap-4 mt-4 text-slate-500 text-sm">
                        <span className="flex items-center gap-1">
                            <Clock size={14} /> Last Sync: {newsData?.metadata?.last_updated}
                        </span>
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                            {newsData?.metadata?.status}
                        </span>
                    </div>
                </div>

                {/* --- CATEGORY GRIDS --- */}
                <div className="space-y-16">

                    {/* Innovations & Breakthroughs */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-amber-100 p-2 rounded-lg text-amber-600"><Zap size={20} /></div>
                            <h2 className="text-2xl font-bold text-slate-800">Global Innovations</h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {newsData?.news?.filter(n => n.category === "Innovation").map((item, idx) => (
                                <NewsCard key={idx} item={item} color="border-t-amber-400" />
                            ))}
                        </div>
                    </section>

                    {/* Hiring & Funding Trends */}
                    <section className="bg-slate-900 -mx-6 px-6 py-16 rounded-[3rem]">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="bg-blue-500 p-2 rounded-lg text-white"><Cpu size={20} /></div>
                            <h2 className="text-2xl font-bold text-white">Market Hiring & Funding</h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {newsData?.news?.filter(n => n.category === "Hiring/Funding").map((item, idx) => (
                                <NewsCard key={idx} item={item} dark={true} />
                            ))}
                        </div>
                    </section>

                    {/* Developer & Community Trends */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600"><Newspaper size={20} /></div>
                            <h2 className="text-2xl font-bold text-slate-800">Developer Trends (Hacker News)</h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {newsData?.news?.filter(n => n.category === "Developer Trends").map((item, idx) => (
                                <SimpleCard key={idx} item={item} />
                            ))}
                        </div>
                    </section>

                    {/*Market Trends */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600"><TrendingDown size={20} /></div>
                            <h2 className="text-2xl font-bold text-slate-800">Market Trends</h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {newsData?.news?.filter(n => n.category === "Market Trends").map((item, idx) => (
                                <SimpleCard key={idx} item={item} />
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </PageLayout>
    );
};

/* --- SUB-COMPONENTS --- */

const NewsCard = ({ item, color = "border-t-blue-500", dark = false }) => (
    <a href={item.url} target="_blank" rel="noreferrer"
        className={`group flex flex-col p-6 rounded-2xl border-t-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl
     ${dark ? 'bg-slate-800 border-transparent text-white' : 'bg-white border-slate-100 text-slate-900'}`}>
        <span className={`text-[10px] font-black uppercase tracking-widest mb-3 ${dark ? 'text-blue-400' : 'text-blue-600'}`}>
            {item.source}
        </span>
        <h3 className="font-bold leading-tight text-lg mb-3 group-hover:text-blue-500 transition-colors">
            {item.title}
        </h3>
        <p className={`text-sm leading-relaxed mb-4 flex-grow ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {item.description}
        </p>
        <div className={`flex items-center gap-2 text-xs font-bold ${dark ? 'text-slate-300' : 'text-slate-400'}`}>
            Read Article <ExternalLink size={12} />
        </div>
    </a>
);

const SimpleCard = ({ item }) => (
    <a href={item.url} target="_blank" rel="noreferrer"
        className="p-4 bg-white border border-slate-100 rounded-xl hover:border-blue-200 transition-colors group">
        <h4 className="text-sm font-bold text-slate-800 leading-snug group-hover:text-blue-600 line-clamp-2">
            {item.title}
        </h4>
        <div className="mt-2 text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
            via {item.source}
        </div>
    </a>
);

export default Insights;